import React, { useEffect, useState, useCallback } from 'react';
import { Typography, Box, Stack, Card, CardContent, Button, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert, Link, Container, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OptionsDropdowns from '../quoter/sail-options/components/OptionsDropdowns';
import CoreSailInfo from '../quoter/sail-options/components/CoreSailInfo';
import { SailTypeName, getSailTypeName } from '../../utils/quoter/sailTypeMapper';
import ConfiguredFeaturesSummary from '../quoter/sail-options/components/ConfiguredFeaturesSummary';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import logger from '../../utils/logging/logger';
import { useVersionState } from '../../hooks/quoter/VersionContext';
import config from '../../config';
import { apiHandler } from '../../utils/api/responseHandler';
import { QuoteData } from '../../types/quoter/types';
import AddToCartButton from '../quoter/cart/AddToCartButton';
import QuoteSummaryPrintModal from '../quoter/sail-options/components/QuoteSummaryPrintModal';
import PrintIcon from '@mui/icons-material/Print';
import SailPrice from '../quoter/sail-options/components/SailPrice';
import LaminateToggle from '../quoter/sail-options/components/LaminateToggle';
import { useAuth } from '../../hooks/auth/useAuth';
import { useComponentVersion } from '../../hooks/quoter/useComponentVersion';
import { usePricing } from '../../hooks/quoter/usePricing';

const componentName = 'SailOptionsContainer';

// Styled component for collapsible section headers
const CollapsibleHeaderStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(2, 2.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    border: '2px solid',
    borderColor: theme.palette.grey[300],
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}05`,
    },
}));

// Styled component for mobile sticky CTA bar
const StickyCTABar = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: '70px',
    backgroundColor: theme.palette.background.paper,
    borderTop: `2px solid ${theme.palette.primary.main}`,
    boxShadow: '0 -4px 12px rgba(0,0,0,0.15)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.up('md')]: {
        display: 'none',
    },
}));

interface SailOptionsContainerProps {
    sailTypeName: SailTypeName;
    boatId: number;
    sailId: number;
    sailArea: number;
    quoteId?: number;
    sailCutId?: number;
    sailDisplayName?: string;
    sailWeightName?: string;
    sail_ImgName?: string;
    isFurling?: number;
}

type Boat = {
    BoatId: number;
    BoatModel: string;
    I: number | null;
    J: number | null;
    P: number | null;
    E: number | null;
    BoatLengthFt: number;
    Boat_ImgName: string | null;
};

const formatMeasurement = (value: number | null | undefined): string => {
    if (typeof value === 'number') {
        return value.toFixed(2);
    }
    return 'N/A';
};

const SailOptionsContainer: React.FC<SailOptionsContainerProps> = (props) => {
    const {
        boatId: boatIdParam,
        sailId: sailIdParam,
        sailArea: sailAreaParam,
        quoteId: quoteIdParam,
        sailCutId: sailCutIdParam,
        isFurling: isFurlingParam
    } = useParams();

    const boatId = Number(boatIdParam || '0');
    const sailId = Number(sailIdParam || '0');
    const sailArea = Number(sailAreaParam || '0');
    const quoteId = Number(quoteIdParam || '0');
    const sailCutId = Number(sailCutIdParam || '0');
    const isFurling = Number(isFurlingParam);
    const sailType = getSailTypeName(sailId);
    const [boat, setBoat] = useState<Boat | null>(null);
    const [loading, setLoading] = useState(false);
    const [sailDisplayName, setSailDisplayName] = useState<string>(props.sailDisplayName || '');
    const [sail_ImgName, setSail_ImgName] = useState<string>(props.sail_ImgName || '');
    const [sailWeightName, setSailWeightName] = useState<string>(props.sailWeightName || '');
    const [selectionSummary, setSelectionSummary] = useState<string>('');

    const { masterVersion, updateMasterVersion } = useVersionState();
    const navigate = useNavigate();

    const [quoteSummaryOpen, setQuoteSummaryOpen] = useState(false);
    const { user, isAnonymous } = useAuth();

    // Reset dialog state
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState<string | null>(null);

    // State for mobile sticky bar pricing
    const [stickyBarQuoteData, setStickyBarQuoteData] = useState<QuoteData | null>(null);

    // Customization expanded by default
    const [customizationOpen, setCustomizationOpen] = useState(true);

    // Fetch quote data for sticky bar pricing
    const fetchStickyBarPricing = useCallback(async () => {
        if (!quoteId) return;
        try {
            const response = await apiHandler.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`,
                { validateFn: (data) => data }
            );
            if (!response.error && response.data) {
                setStickyBarQuoteData(response.data);
            }
        } catch (err) {
            logger.log('LogError', componentName, 'Error fetching sticky bar pricing', err);
        }
    }, [quoteId]);

    useComponentVersion('clothDefault', {
        onVersionChange: async () => {
            await fetchStickyBarPricing();
            await fetchQuoteData();
        }
    });

    useComponentVersion('sailOptions', {
        onVersionChange: async () => {
            await fetchStickyBarPricing();
            await fetchQuoteData();
        }
    });

    useEffect(() => {
        fetchStickyBarPricing();
    }, [fetchStickyBarPricing]);

    const stickyBarPricing = usePricing(stickyBarQuoteData);

    const handleOpenQuoteSummary = () => {
        if (quoteId > 0) {
            setQuoteSummaryOpen(true);
        }
    };

    const handleCloseQuoteSummary = () => {
        setQuoteSummaryOpen(false);
    };

    const handleSelectDifferentSail = () => {
        navigate(`/quoter/sail-selection/${boatId}`);
    };

    const handleResetClick = () => {
        setResetDialogOpen(true);
    };

    const handleResetCancel = () => {
        setResetDialogOpen(false);
    };

    const handleResetConfirm = async () => {
        if (!quoteId || !user?.userId) return;

        setResetLoading(true);
        setResetError(null);

        try {
            const response = await fetch(`${config.API_URL}/api/quotes/${quoteId}/cancel`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ userId: user.userId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to cancel quote');
            }

            setResetDialogOpen(false);
            navigate(`/quoter/create-quote/${boatId}/${sailId}/${sailArea}/${sailCutId}/${isFurling}`);
        } catch (err) {
            logger.log('LogError', componentName, 'Error resetting quote:', err);
            setResetError(err instanceof Error ? err.message : 'Failed to reset quote. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    const handleCloseErrorSnackbar = () => {
        setResetError(null);
    };

    interface ContentBlock {
        ContentHeader: string;
        ContentMain: string;
    }

    const [standardFeaturesContent, setStandardFeaturesContent] = useState<ContentBlock | null>(null);

    const fetchBoatDetails = useCallback(async () => {
        if (!boatId) return;

        setLoading(true);

        try {
            const response = await apiHandler.fetch<Boat>(
                `${config.API_URL}/api/boat/${boatId}`,
                {
                    validateFn: (data) => data,
                }
            );

            if (response.error) throw response.error;
            setBoat(response.data || null);
        } catch (err) {
            console.error("Failed to fetch boat details:", err);
        } finally {
            setLoading(false);
        }
    }, [boatId]);

    const fetchQuoteData = useCallback(async () => {
        if (!quoteId) return;

        try {
            const response = await apiHandler.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`,
                {
                    validateFn: (data) => data,
                }
            );

            if (response.error) throw response.error;

            if (response.data?.SailDisplayName) {
                setSailDisplayName(response.data.SailDisplayName);
            }

            if (response.data?.Sail_ImgName) {
                setSail_ImgName(response.data.Sail_ImgName);
            }

            if (response.data?.SailWeightName) {
                setSailWeightName(response.data.SailWeightName);
            }

            if (response.data?.SelectionSummary) {
                setSelectionSummary(response.data.SelectionSummary);
            }

        } catch (err) {
            console.error("Failed to fetch quote data:", err);
        }
    }, [quoteId]);

    useEffect(() => {
        const fetchStandardFeatures = async () => {
            try {
                const response = await apiHandler.fetch<ContentBlock[]>(
                    `${config.API_URL}/api/content?contentCategoryCode=SAIL_STD_FEATURES&sailId=${sailId}&sailCutId=${sailCutId}&isFurling=${isFurling}`,
                    {
                        validateFn: (data) => data,
                    }
                );

                if (response.error) throw response.error;

                if (response.data && response.data.length > 0) {
                    setStandardFeaturesContent(response.data[0]);
                }
            } catch (err) {
                console.error('Error in fetchStandardFeatures:', err);
            }
        };

        fetchStandardFeatures();
    }, [sailId, sailCutId, isFurling]);

    useEffect(() => {
        fetchBoatDetails();
    }, [fetchBoatDetails]);

    useEffect(() => {
        if (masterVersion === 0) {
            updateMasterVersion({
                requestingComponent: 'sailOptionsContainer',
                currentVersion: 0
            });
        }
    }, [masterVersion, updateMasterVersion]);

    useEffect(() => {
        fetchQuoteData();
    }, [fetchQuoteData]);

    return (
        <Box sx={{ pb: { xs: '80px', md: 2 } }}>
            <Container maxWidth="lg" sx={{ pt: 1 }}>
                {boat && (
                    <>
                        {/* ===== SECTION 1: PRODUCT IDENTITY HEADER ===== */}
                        <Stack spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    textAlign: 'center'
                                }}
                            >
                                {boat.BoatModel}
                            </Typography>

                            {/* Measurements */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center'
                                }}
                            >
                                I: {formatMeasurement(boat.I)}, J: {formatMeasurement(boat.J)}, P: {formatMeasurement(boat.P)}, E: {formatMeasurement(boat.E)}
                            </Typography>

                            <Button
                                onClick={handleSelectDifferentSail}
                                variant="outlined"
                                size="small"
                                sx={{
                                    mt: 0.5,
                                    textTransform: 'none'
                                }}
                            >
                                SELECT A DIFFERENT SAIL
                            </Button>
                        </Stack>

                        {/* Contact Banner - Top */}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'grey.300',
                                borderRadius: 1,
                                p: 1.5,
                                mb: 2.5,
                                textAlign: 'center',
                                bgcolor: 'grey.50'
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" component="span">
                                Have questions?{' '}
                            </Typography>
                            <Link
                                href="mailto:crew@pointsails.com"
                                sx={{
                                    color: '#FF6B35',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Email
                            </Link>
                            <Typography variant="body2" color="text.secondary" component="span">
                                {' '}or call us at{' '}
                            </Typography>
                            <Link
                                href="tel:+13609037268"
                                sx={{
                                    color: '#FF6B35',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                (360) 903-7268
                            </Link>
                            <Typography variant="body2" color="text.secondary" component="span">
                                {' '}/{' '}
                            </Typography>
                            <Link
                                href="tel:+16196639330"
                                sx={{
                                    color: '#FF6B35',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                (619) 663-9330
                            </Link>
                            <Typography variant="body2" color="text.secondary" component="span">
                                {' '}&mdash; we're here to help!
                            </Typography>
                        </Box>

                        {/* ===== SECTION 2: PRIMARY COMMITMENT ZONE ===== */}
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: 4, 
                                mb: 5, 
                                alignItems: 'stretch' 
                            }}
                        >
                            {/* Left Column: Sail Image + Standard Features */}
                            <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 calc(58.33% - 50px)' }, display: 'flex', flexDirection: 'column' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        bgcolor: 'grey.50',
                                        borderRadius: 2,
                                        p: 4,
                                        flex: 1,
                                        minHeight: { md: 400 },
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    {sail_ImgName ? (
                                        <Box
                                            component="img"
                                            src={`/images/SailDrawings/${sail_ImgName}`}
                                            alt={sailDisplayName || 'Sail'}
                                            sx={{
                                                maxWidth: '100%',
                                                maxHeight: { xs: 350, md: 450 },
                                                width: 'auto',
                                                height: 'auto',
                                                objectFit: 'contain'
                                            }}
                                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                console.error("Image failed to load:", e.currentTarget.src);
                                            }}
                                        />
                                    ) : (
                                        <Typography color="text.secondary">
                                            No image available
                                        </Typography>
                                    )}
                                </Box>

                                {/* Standard Features Section - under sail image */}
                                {standardFeaturesContent && (
                                    <Box sx={{ mt: 2, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                                            {standardFeaturesContent.ContentHeader || (sailDisplayName + ' Standard Features')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {standardFeaturesContent.ContentMain}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Right Column: Ready to Order */}
                            <Box sx={{ flex: { xs: '1 1 auto', md: '1 1 calc(41.67% + 50px)' }, display: 'flex' }}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        border: 2,
                                        borderColor: 'primary.main',
                                        borderRadius: 2,
                                        p: 2,
                                        pb: 1.5,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CardContent sx={{ p: 0 }}>
                                        <Stack spacing={1.5} alignItems="center">
                                            {/* Sail Name */}
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    textAlign: 'center',
                                                    color: 'text.primary',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {sailDisplayName}
                                            </Typography>

                                            {/* CoreSailInfo - Area and LP% for headsails */}
                                            <Box sx={{ textAlign: 'center' }}>
                                                <CoreSailInfo
                                                    quoteId={quoteId}
                                                    sailId={sailId}
                                                    showLp={sailType === 'headsail'}
                                                />
                                            </Box>

                                            {/* Price */}
                                            <Box sx={{ textAlign: 'center' }}>
                                                <SailPrice quoteId={quoteId} variant="simple" />
                                            </Box>

                                            {/* Laminate/Dacron Toggle - only renders for tri-radial mainsails/headsails */}
                                            <LaminateToggle
                                                quoteId={quoteId}
                                                sailId={sailId}
                                                sailCutId={sailCutId}
                                            />

                                            {/* Divider */}
                                            <Divider sx={{ width: '100%' }} />

                                            {/* Default validation - moved above configuration */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Defaults recommended by sailmaker
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Customize your options below
                                                </Typography>
                                            </Box>

                                            {/* Included Configuration */}
                                            <Box sx={{ width: '100%' }}>
                                                <ConfiguredFeaturesSummary
                                                    quoteId={quoteId}
                                                    sailId={sailId}
                                                    compactOnMobile={false}
                                                />
                                            </Box>

                                            {/* Primary CTA */}
                                            <Box sx={{ width: '100%', maxWidth: 300, pt: 2 }}>
                                                <AddToCartButton
                                                    key={`cart-${user.userId}-${isAnonymous}`}
                                                    quoteId={quoteId}
                                                    boatId={boatId}
                                                    fullWidth
                                                    size="large"
                                                />
                                            </Box>

                                            {/* Safety reassurance */}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textAlign: 'center',
                                                    color: 'secondary.main',
                                                    fontStyle: 'italic'
                                                }}
                                            >
                                                A sailmaker will review your selections.<br />
                                                No payment until you approve.
                                            </Typography>

                                            {/* Print Summary */}
                                            <Button
                                                onClick={handleOpenQuoteSummary}
                                                startIcon={<PrintIcon />}
                                                variant="text"
                                                size="small"
                                                disabled={quoteId <= 0}
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                Print Summary
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>

                        {/* ===== SECTION 3: OPTIONAL CUSTOMIZATION ZONE ===== */}
                        <Box>
                            {/* Main Customization Toggle */}
                            <CollapsibleHeaderStyled 
                                onClick={() => setCustomizationOpen(!customizationOpen)}
                                sx={customizationOpen ? { 
                                    borderRadius: '8px 8px 0 0',
                                    marginBottom: 0,
                                    borderBottom: 'none'
                                } : {}}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight={600}>
                                        Customize this sail
                                    </Typography>
                                </Box>
                                {customizationOpen && (
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResetClick();
                                        }}
                                        startIcon={<RestartAltIcon />}
                                        variant="text"
                                        size="small"
                                        disabled={quoteId <= 0}
                                        sx={{ 
                                            color: 'text.secondary',
                                            textTransform: 'none',
                                            mr: 2
                                        }}
                                    >
                                        Reset to Defaults
                                    </Button>
                                )}
                                <ExpandMoreIcon
                                    sx={{
                                        transform: customizationOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease'
                                    }}
                                />
                            </CollapsibleHeaderStyled>

                            <Collapse in={customizationOpen}>
                                <Box 
                                    sx={{ 
                                        border: '2px solid',
                                        borderColor: 'grey.300',
                                        borderTop: 'none',
                                        borderRadius: '0 0 8px 8px',
                                        px: 3,
                                        py: 3,
                                        backgroundColor: 'white'
                                    }}
                                >
                                    {/* Options */}
                                    <OptionsDropdowns
                                        quoteId={quoteId}
                                        boatId={boatId}
                                        sailId={sailId}
                                        sailArea={sailArea}
                                        isFurling={isFurling}
                                    />
                                </Box>
                            </Collapse>

                        </Box>
                    </>
                )}
            </Container>

            {/* Mobile Sticky CTA Bar */}
            <StickyCTABar>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {stickyBarPricing ? (
                        <>
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                Price
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ lineHeight: 1.2 }}>
                                {stickyBarPricing.finalPrice ? `$${Math.round(stickyBarPricing.finalPrice).toLocaleString()}` : 'Contact Us'}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Loading...
                        </Typography>
                    )}
                </Box>
                <AddToCartButton
                    key={`cart-mobile-${user.userId}-${isAnonymous}`}
                    quoteId={quoteId}
                    boatId={boatId}
                    size="large"
                />
            </StickyCTABar>

            <QuoteSummaryPrintModal
                open={quoteSummaryOpen}
                onClose={handleCloseQuoteSummary}
                quoteId={quoteId}
            />

            {/* Reset Confirmation Dialog */}
            <Dialog
                open={resetDialogOpen}
                onClose={handleResetCancel}
                aria-labelledby="reset-dialog-title"
            >
                <DialogTitle id="reset-dialog-title">
                    Reset to Default Options?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reset? All your current selections will be lost and a new quote will be created with default options.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleResetCancel} disabled={resetLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResetConfirm}
                        color="primary"
                        variant="contained"
                        disabled={resetLoading}
                        autoFocus
                    >
                        {resetLoading ? 'Resetting...' : 'Reset'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Snackbar */}
            <Snackbar
                open={!!resetError}
                autoHideDuration={6000}
                onClose={handleCloseErrorSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
                    {resetError}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SailOptionsContainer;
